import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  toast
} from '@sparrowengg/twigs-react';
import {
  ArrowLeftIcon,
  ClockIcon,
  DeleteIcon
} from '@sparrowengg/twigs-react-icons';
import CodeIcon from 'commons/icons/code';
import React, { useState, useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import ActionEnergy from 'commons/icons/action-energy';
import CodeSpark from 'commons/icons/code-spark';
import {
  getHeader,
  getLocalStorageValue,
  isValidJSON,
  setLocalStorageValue
} from 'commons/helper';
import { CodeBlockProps, EventsType, FieldType } from '../types';
import axios from 'lib/axios';
import { useSelector } from 'react-redux';
import { IRootDispatch, IRootState, RefDivType } from 'commons/types';
import * as monaco from 'monaco-editor';
import { APP_STORE } from 'commons/constants';
import { setAppValidation, setSimulateCode } from 'redux-store/actions';
import { useDispatch } from 'react-redux';

const CodeBlock = ({
  currentField,
  events,
  eventFields,
  handleEventFields,
  codeBlockRef,
  consoleRef,
  eventSimulationRef,
  handleHeaderBtnLoader
}: CodeBlockProps) => {
  const [showSimulatePage, setShowSimulatePage] = useState(false);
  const [consoleOutputs, setConsoleOutputs] = useState<
    { message: string; isError: boolean }[]
  >([]);
  const currentApp = useSelector((state: IRootState) => state.currentApp);
  const appValidation = useSelector((state: IRootState) => state.appValidation);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const editorContainerRef = useRef(null);
  const dispatch = useDispatch<IRootDispatch>();

  useEffect(() => {
    if (appValidation?.message) {
      setConsoleOutputs((prev) => [
        ...prev,
        { message: appValidation?.message ?? '', isError: false }
      ]);
    } else if (appValidation?.error) {
      setConsoleOutputs((prev) => [
        ...prev,
        { message: appValidation?.error ?? '', isError: true }
      ]);
    }
  }, [appValidation]);

  useEffect(() => {
    let resizeTimeout: ReturnType<typeof setTimeout>;

    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.layout();
        }
      }, 300);
    };

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    if (editorContainerRef.current) {
      resizeObserver.observe(editorContainerRef.current);
    }

    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeObserver.disconnect();
    };
  }, [showSimulatePage]);

  useEffect(() => {
    if (!!currentField.field) {
      setShowSimulatePage(true);
    }
  }, [currentField.field]);

  return (
    <Flex flexDirection="column" css={{ width: '100%' }}>
      <Box css={{ height: 557, background: '#151515' }} ref={codeBlockRef}>
        {/* This is an empty div that targets the mounted component for the tour. */}
        <Box ref={eventSimulationRef} css={{ opacity: 0 }} />

        {!!currentField.field ? (
          <Flex css={{ height: '100%', width: 'calc(100vw - 280px)' }}>
            <Box
              css={{
                height: '100%',
                width: !showSimulatePage
                  ? 'calc(100vw - 280px)'
                  : 'calc(100vw - 579px - 280px)',
                transition: 'all .3s ease'
              }}
            >
              <Flex
                alignItems="center"
                css={{
                  padding: `${showSimulatePage ? '$11' : '$6'} $12`,
                  borderBottom: '$borderWidths$xs solid $white200'
                }}
                justifyContent="space-between"
              >
                <Flex alignItems="center" gap="$8">
                  <CodeSpark />
                  <Text
                    css={{ color: '$primary400' }}
                    size="sm"
                  >{`${currentField.field}: async function (args) {`}</Text>
                </Flex>
                {!showSimulatePage && (
                  <IconButton
                    icon={<ActionEnergy color="#36737A" />}
                    size="lg"
                    onClick={() => setShowSimulatePage(true)}
                    variant="ghost"
                  />
                )}
              </Flex>
              <Box
                ref={editorContainerRef}
                css={{
                  height: 'calc(100% - 128px)',
                  paddingBlock: '$3',
                  width: '100%'
                }}
              >
                <Editor
                  height="100%"
                  width="100%"
                  defaultLanguage="typescript"
                  options={{
                    fontFamily: 'monospace',
                    fontSize: 16,
                    lineHeight: 20,
                    renderLineHighlight: 'none'
                  }}
                  value={
                    eventFields.find(
                      (field) => field.field === currentField.field
                    )?.code ?? ''
                  }
                  onChange={(value) => {
                    if (
                      value !==
                      eventFields.find(
                        (field) => field.field === currentField.field
                      )?.code
                    ) {
                      dispatch(
                        setAppValidation({
                          isValidated: false,
                          message: ''
                        })
                      );
                    }
                    handleEventFields(
                      eventFields.map((field) => {
                        if (field.field === currentField.field) {
                          return { ...field, code: value ?? '' };
                        }
                        return field;
                      })
                    );
                  }}
                  beforeMount={(monaco) => {
                    monaco.editor.defineTheme('custom-theme', {
                      base: 'vs-dark',
                      inherit: true,
                      rules: [],
                      colors: {
                        'editor.background': '#151515',
                        'editor.lineHighlightBackground': '#151515',
                        'editorGutter.background': '#151515'
                      }
                    });

                    monaco.languages.register({ id: 'javascript' });

                    (monaco as any).languages.registerCompletionItemProvider(
                      'javascript',
                      {
                        provideCompletionItems: () => {
                          return [
                            {
                              label: 'let',
                              kind: monaco.languages.CompletionItemKind.Keyword
                            },
                            {
                              label: 'const',
                              kind: monaco.languages.CompletionItemKind.Keyword
                            },
                            {
                              label: 'var',
                              kind: monaco.languages.CompletionItemKind.Keyword
                            }
                            // Add more completion items as needed
                          ];
                        }
                      }
                    );
                  }}
                  onMount={(editor, monaco) => {
                    editorRef.current = editor;
                    monaco.editor.setTheme('custom-theme');
                  }}
                />
              </Box>
              <Flex
                alignItems="center"
                css={{
                  padding: '$11 $12',
                  borderTop: '$borderWidths$xs solid $white200'
                }}
                justifyContent="space-between"
              >
                <Flex alignItems="center" gap="$8">
                  <CodeSpark />
                  <Text css={{ color: '$primary400' }} size="sm">{`}`}</Text>
                </Flex>
              </Flex>
            </Box>
            {showSimulatePage ? (
              <SimulateCode
                setConsoleOutputs={setConsoleOutputs}
                setShowSimulatePage={setShowSimulatePage}
                currentApp={currentApp}
                currentField={currentField}
                events={events}
                handleHeaderBtnLoader={handleHeaderBtnLoader}
              />
            ) : null}
          </Flex>
        ) : (
          <Flex
            alignItems="center"
            justifyContent="center"
            css={{ height: '100%' }}
          >
            <CodeIcon color="rgba(255, 255, 255, 0.2)" size={200} />
          </Flex>
        )}
      </Box>
      <ConsoleOutput
        consoleOutputs={consoleOutputs}
        consoleRef={consoleRef}
        clearConsoleOutputs={() => setConsoleOutputs([])}
      />
    </Flex>
  );
};

const SimulateCode = ({
  setShowSimulatePage,
  currentApp,
  currentField,
  setConsoleOutputs,
  events,
  handleHeaderBtnLoader
}: {
  setShowSimulatePage: React.Dispatch<React.SetStateAction<boolean>>;
  currentApp: IRootState['currentApp'];
  currentField: FieldType;
  events: EventsType[];
  setConsoleOutputs: React.Dispatch<
    React.SetStateAction<{ message: string; isError: boolean }[]>
  >;
  handleHeaderBtnLoader: (value: boolean) => void;
}) => {
  const [isJsonError, setIsJsonError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loader, setLoader] = useState(false);
  const isAppValidated = useSelector(
    (state: IRootState) => state.appValidation.isValidated
  );
  const dispatch = useDispatch<IRootDispatch>();
  const eventFields = useSelector((state: IRootState) => state.eventFields);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const simulateCode = eventFields.find(
    (field) => field.field === currentField.field
  )?.simulateCode;

  const handleEditorDidMount = (
    editor: monaco.editor.IStandaloneCodeEditor | null
  ) => {
    editorRef.current = editor;
    // Format the JSON after the editor has mounted
    !!editor?.getAction &&
      editor.getAction('editor.action.formatDocument')?.run();
  };

  const simulateCurrentCode = async (event: {}) => {
    setLoader(true);
    handleHeaderBtnLoader(true);
    try {
      const appStore = getLocalStorageValue(APP_STORE);
      const response = (
        await axios.post(
          `/ssdk/run?appVersionId=${currentApp?.appVersionId}&eventName=${currentField.field}`,
          {
            event,
            ...(appStore && { store: appStore })
          },
          getHeader()
        )
      )?.data;
      if (response.store) {
        setLocalStorageValue(APP_STORE, response.store);
      }
      (response?.consoleLogs ?? []).forEach((logs: string) =>
        setConsoleOutputs((prev) => [
          ...prev,
          { message: logs, isError: false }
        ])
      );
      (response?.consoleErrors ?? []).forEach((logs: string) =>
        setConsoleOutputs((prev) => [...prev, { message: logs, isError: true }])
      );
    } catch (error) {
      console.error(error);
      toast({
        variant: 'error',
        description: 'Error when simulating the code'
      });
    } finally {
      setLoader(false);
      handleHeaderBtnLoader(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    let currentCode;
    if (!simulateCode) {
      const newCode = JSON.stringify(
        events.reduce(
          (acc, currentEvent) =>
            acc ||
            currentEvent.events.find(
              (event) => event.name === currentField.field
            )?.payload,
          undefined
        ) ?? {}
      );
      dispatch(setSimulateCode(`${currentField.field}`, newCode));
      currentCode = newCode;
    } else {
      currentCode = simulateCode;
    }

    if (editorRef?.current) {
      editorRef.current && editorRef.current.setValue(currentCode as string);
      setTimeout(() => {
        !!editorRef.current &&
          editorRef.current.getAction('editor.action.formatDocument')?.run();
      }, 0);
    }
    setLoading(false);
  }, [currentField.field, events]);

  return (
    <Box
      css={{
        height: '100%',
        maxWidth: 579,
        width: '100%',
        borderLeft: '$borderWidths$xs solid $white200'
      }}
    >
      <Flex
        css={{
          padding: '$6 $8',
          borderBottom: '$borderWidths$xs solid $white200'
        }}
        justifyContent="space-between"
      >
        <Flex alignItems="center" gap="$6">
          <IconButton
            icon={<ArrowLeftIcon />}
            size="lg"
            variant="ghost"
            color="light"
            onClick={() => setShowSimulatePage(false)}
          />
          <Text size="lg" weight="medium">
            Event Payload
          </Text>
        </Flex>
        <Flex alignItems="center" gap="$8">
          {isJsonError ? (
            <Flex gap="$4" alignItems="center">
              <Box
                css={{
                  backgroundColor: '$negative600',
                  width: '6px',
                  height: '6px',
                  borderRadius: '$round'
                }}
              />
              <Text css={{ color: '$white600' }} size="xs">
                Invalid JSON Structure
              </Text>
            </Flex>
          ) : null}
          <Button
            size="md"
            variant="ghost"
            disabled={
              isJsonError || !currentApp?.appId || loader || !isAppValidated
            }
            loading={loader}
            onClick={() => {
              simulateCurrentCode(simulateCode as string);
            }}
            rightIcon={<ActionEnergy color="#3B8088" />}
          >
            Simulate
          </Button>
        </Flex>
      </Flex>
      <Box
        css={{
          paddingBlock: '$3',
          height: 'calc(100% - 64px)',
          width: 579
        }}
      >
        <Editor
          height="100%"
          width="100%"
          defaultLanguage="json"
          defaultValue={simulateCode}
          value={simulateCode}
          loading={loading}
          onChange={(value) => {
            if (value) {
              setIsJsonError(!isValidJSON(value));
              dispatch(setSimulateCode(`${currentField.field}`, value));
            }
          }}
          options={{
            fontFamily: 'monospace',
            scrollbar: {
              vertical: 'hidden',  
              horizontal: 'hidden' 
            }
          }}
          beforeMount={(monaco) => {
            handleEditorDidMount(monaco.editor as any);
            monaco.editor.defineTheme('custom-theme', {
              base: 'vs-dark',
              inherit: true,
              rules: [],
              colors: {
                'editor.background': '#151515',
                'editor.lineHighlightBackground': '#151515',
                'editorGutter.background': '#151515'
              }
            });
          }}
          onMount={(editor, monaco) => {
            handleEditorDidMount(editor);
            if (editorRef?.current) {
              editorRef.current = editor;
            }
            monaco.editor.setTheme('custom-theme');
            editor.updateOptions({
              fontFamily: 'monospace',
              fontSize: 12,
              fontLigatures: true,
              lineHeight: 20
            });
          }}
        />
      </Box>
    </Box>
  );
};

const ConsoleOutput = ({
  consoleOutputs,
  consoleRef,
  clearConsoleOutputs
}: {
  consoleOutputs: { message: string; isError: boolean }[];
  consoleRef: RefDivType;
  clearConsoleOutputs: () => void;
}) => {
  return (
    <Box css={{ height: '100%' }} ref={consoleRef}>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        css={{ padding: '$4 $6' }}
      >
        <Flex gap="$2" alignItems="center">
          <ClockIcon color="#56B0BB" size={16} />
          <Text size="sm">Console</Text>
        </Flex>
        <Button
          onClick={() => clearConsoleOutputs()}
          size="md"
          rightIcon={<DeleteIcon />}
          variant="ghost"
          color="light"
          css={{ background: '0 !important' }}
        >
          Clear
        </Button>
      </Flex>
      <Box
        css={{
          padding: '$12',
          height: 'calc(100vh - 686px)',
          overflow: 'auto'
        }}
      >
        {consoleOutputs.length ? (
          <Flex flexDirection="column" gap="$1">
            {consoleOutputs.map((logs) => {
              return (
                <Text
                  size="sm"
                  css={{
                    color: logs.isError ? '$negative400' : '$primary400',
                    wordWrap: 'break-word',
                    textWrap: 'balance',
                    whiteSpace: 'break-spaces',
                    width: '100%',
                    wordBreak: "break-all"
                  }}
                >
                  {logs.message}
                </Text>
              );
            })}
          </Flex>
        ) : (
          <Text css={{ color: '$white700' }} size="sm">
            The output will be displayed here...
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default CodeBlock;
